import * as React from 'react';
import MuiTable from './MuiTable';
import { Box, Typography } from '@mui/material';
import { ILabel } from '../constants/interfaces';
import {
  evaluateNumericFormula,
  sumMonthDiffsAcrossRows,
} from '../utils/formulaUtils';
import FormField from './FormField';
import { formUi } from '../constants/globalcss';

interface TableSectionProps {
  onValueChange: (value: string, children?: unknown) => void;
  visibleFields?: ILabel[];
  field: ILabel;
  resetTrigger?: number;
}

const TableSectionView: React.FC<{
  field: ILabel;
  columns: ILabel[] | undefined;
  rows: Array<Record<string, string>>;
  handleRowsChange: React.Dispatch<
    React.SetStateAction<Array<Record<number, string>>>
  >;
  onValueChange: (value: string, children: unknown) => void;
  resetTrigger: number;
}> = ({
  field,
  columns,
  rows,
  handleRowsChange,
  onValueChange,
  resetTrigger,
}) => {
  const formula = field.formula;

  const calculated = React.useMemo(() => {
    if (!columns || !formula) return '';

    const monthDiffMatch = formula.match(/^MONTH_DIFF\((\d+),\s*(\d+)\)$/i);
    if (monthDiffMatch) {
      const colId1 = Number(monthDiffMatch[1]);
      const colId2 = Number(monthDiffMatch[2]);
      return sumMonthDiffsAcrossRows(colId1, colId2, rows);
    }

    const total = rows.reduce((acc, row) => {
      const rowTotal = evaluateNumericFormula(formula, row);
      return acc + (isNaN(rowTotal) ? 0 : rowTotal);
    }, 0);

    return total;
  }, [rows, formula, columns]);

  const children = React.useMemo(
    () =>
      rows.map((row) =>
        Object.entries(row).map(([labelId, value]) => ({
          labelId: Number(labelId),
          response: value,
        }))
      ),
    [rows]
  );

  const lastValueRef = React.useRef<{
    calculated: string;
    children: unknown;
  } | null>(null);

  React.useEffect(() => {
    const last = lastValueRef.current;
    const isSame =
      last &&
      last.calculated === calculated &&
      JSON.stringify(last.children) === JSON.stringify(children);

    if (!isSame) {
      onValueChange(calculated, children);
      lastValueRef.current = { calculated, children };
    }
  }, [calculated, children, onValueChange]);

  return (
    <Box
      sx={{
        backgroundColor: formUi.card.background,
        border: formUi.card.border,
        borderRadius: formUi.card.radius,
        boxShadow: formUi.card.shadow,
        p: formUi.spacing.cardPadding,
        width: '100%',
      }}
    >
      <Typography
        sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}
        variant="subtitle2"
      >
        {field.text}
      </Typography>
      {columns && (
        <MuiTable
          columns={columns}
          resetTrigger={resetTrigger}
          onRowsChange={handleRowsChange}
        />
      )}
      <FormField
        value={calculated.toLocaleString('en-US', {
          currency: 'USD',
          minimumFractionDigits: 2,
          style: 'currency',
        })}
        errorText=""
        isLabelHidden={false}
        displayOnly
        field={{ ...field, type: 'text' } as ILabel}
      />
    </Box>
  );
};

// Stateful wrapper
const TableSection: React.FC<TableSectionProps> = ({
  field,
  onValueChange,
  resetTrigger = 0, // default value
}) => {
  const [rows, setRows] = React.useState<Array<Record<number, string>>>([]);
  const columns = React.useMemo(() => field.children, [field.children]);
  const handleRowsChange = React.useCallback(setRows, [setRows]);

  return (
    <TableSectionView
      field={field}
      columns={columns}
      rows={rows}
      resetTrigger={resetTrigger}
      handleRowsChange={handleRowsChange}
      onValueChange={onValueChange}
    />
  );
};

export default TableSection;
